import { apiClient } from "@/lib/api";
import type {
  BookingLifecycleStatus,
  PaginationParams,
  RegionCode,
} from "@/types";

export type CustomerSupportChatStatus = "OPEN" | "CLOSED";

export interface CustomerSupportChatMessage {
  id: string;
  conversation_id: string;
  content: string;
  channel: string;
  is_from_admin: boolean;
  sender_user_id: string | null;
  delivery_status: string | null;
  delivered_at: string | null;
  created_at: string;
}

export interface CustomerSupportChatListItem {
  id: string;
  customer_user_id: string | null;
  customer_channel: string;
  customer_contact: string;
  customer_language: string;
  status: CustomerSupportChatStatus;
  is_guest: boolean;
  latest_message_preview: string | null;
  latest_message_at: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

export interface CustomerSupportChatDetail {
  id: string;
  customer_user_id: string | null;
  customer_channel: string;
  customer_contact: string;
  customer_language: string;
  status: CustomerSupportChatStatus;
  is_guest: boolean;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  messages: CustomerSupportChatMessage[];
}

export interface CustomerSupportChatListResponse {
  total: number;
  items: CustomerSupportChatListItem[];
}

export interface CustomerSupportChatContextBooking {
  id: string;
  status: BookingLifecycleStatus;
  visit_at: string | null;
  time_slot: string | null;
  activity_name: string | null;
  activity_region_code: RegionCode;
  requires_handshake: boolean;
  handshake_session_id: string | null;
  can_amend: boolean;
  can_reopen_handshake: boolean;
}

export interface CustomerSupportChatContextHandshakeMessage {
  content: string;
  sender_type: string | null;
  created_at: string;
}

export interface CustomerSupportChatContextHandshake {
  id: string;
  booking_id: string;
  status: string;
  activity_name: string | null;
  messages: CustomerSupportChatContextHandshakeMessage[];
}

export interface CustomerSupportChatContext {
  bookings: CustomerSupportChatContextBooking[];
  handshakes: CustomerSupportChatContextHandshake[];
}

export const customerSupportChatKeys = {
  all: ["customerSupportChats"] as const,
  lists: () => [...customerSupportChatKeys.all, "list"] as const,
  list: (params: Record<string, string | number | boolean | undefined>) =>
    [...customerSupportChatKeys.lists(), params] as const,
  details: () => [...customerSupportChatKeys.all, "detail"] as const,
  detail: (id: string) => [...customerSupportChatKeys.details(), id] as const,
  context: (id: string) =>
    [...customerSupportChatKeys.all, "context", id] as const,
};

export const customerSupportChatService = {
  list: async (
    params?: PaginationParams & {
      search?: string;
      status?: CustomerSupportChatStatus;
      open_only?: boolean;
    },
  ): Promise<CustomerSupportChatListResponse> => {
    const response = await apiClient.get<CustomerSupportChatListResponse>(
      "/customer-support-chats",
      { params },
    );
    return response.data;
  },

  getById: async (chatId: string): Promise<CustomerSupportChatDetail> => {
    const response = await apiClient.get<CustomerSupportChatDetail>(
      `/customer-support-chats/${chatId}`,
    );
    return response.data;
  },

  getContext: async (chatId: string): Promise<CustomerSupportChatContext> => {
    const response = await apiClient.get<CustomerSupportChatContext>(
      `/customer-support-chats/${chatId}/context`,
    );
    return response.data;
  },

  sendMessage: async (
    chatId: string,
    content: string,
  ): Promise<CustomerSupportChatMessage> => {
    const response = await apiClient.post<CustomerSupportChatMessage>(
      `/customer-support-chats/${chatId}/messages`,
      { content },
    );
    return response.data;
  },

  close: async (chatId: string): Promise<CustomerSupportChatDetail> => {
    const response = await apiClient.post<CustomerSupportChatDetail>(
      `/customer-support-chats/${chatId}/close`,
    );
    return response.data;
  },

  reopenHandshake: async (
    chatId: string,
    sessionId: string,
  ): Promise<{ id: string }> => {
    const response = await apiClient.post<{ id: string }>(
      `/customer-support-chats/${chatId}/reopen-handshake`,
      { session_id: sessionId },
    );
    return response.data;
  },
};
